/**
 * Os dois lados de um diff unificado, para o editor de comparação do painel.
 *
 * **Por que reconstruir, em vez de mostrar o patch.** Medido em M0-20: o cartão
 * de edição usa o `DiffEditor` do Monaco com `original` e `modified` — dois
 * textos, não um patch. O Codex entrega a outra forma: `FileUpdateChange.diff`
 * é uma string em formato unificado (lido do binding, não suposto). Sem esta
 * ponte, o cartão mostraria o patch cru como se fosse código.
 *
 * **Por que `null` e nunca exceção.** Este texto vem do outro lado e alimenta o
 * cartão de uma sessão que está PARADA esperando decisão. Um `ServerRequest`
 * do app-server não tem timeout: se o cartão estourar ao desenhar, o botão de
 * aprovar não aparece, ninguém responde, e a thread fica travada para sempre.
 * `null` significa "não sei separar isto em dois lados" — e aí o painel mostra
 * o texto como veio, que é feio e funciona.
 */

export interface LadosDoDiff {
  antes: string;
  depois: string;
}

/** `@@ -1,3 +1,4 @@` — o cabeçalho que abre cada trecho. */
const CABECALHO_DE_TRECHO = /^@@ /;

/**
 * Reconstrói `{antes, depois}` de um diff unificado.
 *
 * O que sai NÃO é o arquivo: é o pedaço que o diff mostra. Um patch só carrega
 * os trechos alterados mais algumas linhas de contexto, então reconstruir o
 * arquivo inteiro a partir dele é impossível — e prometer isso faria alguém ler
 * "o arquivo tem 12 linhas" onde tem mil. O nome da função diz isso: são os
 * LADOS do diff.
 */
export function ladosDoDiffUnificado(diff: string): LadosDoDiff | null {
  if (typeof diff !== 'string' || diff.trim() === '') return null;

  const linhas = diff.split('\n').map((l) => (l.endsWith('\r') ? l.slice(0, -1) : l));
  /*
    O `\n` final de todo arquivo de texto vira um elemento vazio no `split`, e
    ele NÃO é uma linha do diff. Sem tirar, um patch que termina normalmente
    ganhava uma linha em branco nos dois lados — e, pior, um cabeçalho sem
    conteúdo nenhum passava a "ter conteúdo" e deixava de degradar para `null`.
    Foi o teste do cabeçalho vazio que acusou.
  */
  if (linhas.length > 0 && linhas[linhas.length - 1] === '') linhas.pop();

  const cabecalhos = linhas.filter((l) => CABECALHO_DE_TRECHO.test(l)).length;
  // Sem nenhum `@@` não é diff unificado: é texto solto, e separar em dois
  // lados seria inventar uma diferença que ninguém escreveu.
  if (cabecalhos === 0) return null;

  const antes: string[] = [];
  const depois: string[] = [];
  let dentroDeTrecho = false;
  let conteudo = 0;

  for (const linha of linhas) {
    if (CABECALHO_DE_TRECHO.test(linha)) {
      dentroDeTrecho = true;
      /*
        Com MAIS DE UM trecho, o cabeçalho entra nos dois lados. Sem ele, um
        trecho da linha 3 e outro da linha 200 apareceriam colados, e quem
        olhasse leria como se fossem vizinhos. O cabeçalho é sintaxe de diff de
        verdade, não marca inventada, e entra nos dois lados para o editor não
        desalinhar. Com um trecho só ele não entra: seria ruído no caso comum.
      */
      if (cabecalhos > 1) {
        antes.push(linha);
        depois.push(linha);
      }
      continue;
    }
    if (!dentroDeTrecho) continue;

    // `\ No newline at end of file` é anotação do formato, não conteúdo.
    if (linha.startsWith('\\')) continue;

    if (linha.startsWith(' ')) {
      antes.push(linha.slice(1));
      depois.push(linha.slice(1));
      conteudo += 1;
    } else if (linha.startsWith('-')) {
      antes.push(linha.slice(1));
      conteudo += 1;
    } else if (linha.startsWith('+')) {
      depois.push(linha.slice(1));
      conteudo += 1;
    } else if (linha === '') {
      // Linha de contexto VAZIA: muitos geradores omitem o espaço à esquerda
      // quando a linha do arquivo é vazia. Descartar comeria linhas em branco
      // do código, e o editor mostraria diferença onde não há.
      antes.push('');
      depois.push('');
      conteudo += 1;
    } else {
      // Qualquer outra coisa encerra o trecho; o que vier depois só volta a
      // contar num `@@` novo.
      dentroDeTrecho = false;
    }
  }

  // Cabeçalho sem nenhuma linha de conteúdo não rende dois lados — renderia
  // dois painéis vazios, que parecem "arquivo apagado" sem ser.
  if (conteudo === 0) return null;

  return { antes: antes.join('\n'), depois: depois.join('\n') };
}
